import { useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Link2, Loader2, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = '/api';

const SubmitMeetup = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState('');
  const [inferredData, setInferredData] = useState(null);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!url) {
      toast.error('Please enter a URL');
      return;
    }
    
    // Basic URL validation
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      toast.error('Please enter a valid URL starting with http:// or https://');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/meetup-submissions/auto`, { url });
      setInferredData(response.data.inferred);
      setSubmitted(true);
      toast.success('Meetup submitted successfully!');
    } catch (error) {
      console.error('Error submitting meetup:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit. Please check the URL and try again.');
    } finally {
      setLoading(false);
    }
  };
  
  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md mx-auto text-center" role="status">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-6" strokeWidth={1.5} />
          <h2 className="text-2xl font-heading font-bold text-foreground mb-3">
            Submitted!
          </h2>
          <p className="text-muted-foreground mb-6">
            We'll review and start importing your events once approved.
          </p>
          
          {inferredData && (
            <div className="bg-muted rounded-lg p-4 mb-6 text-left">
              <p className="text-xs text-muted-foreground mb-2">We detected:</p>
              <p className="font-medium text-foreground">{inferredData.name || 'Unknown Group'}</p>
              {inferredData.city && (
                <p className="text-sm text-muted-foreground">{inferredData.city}{inferredData.state ? `, ${inferredData.state}` : ''}</p>
              )}
              {inferredData.description && (
                <p className="text-xs text-muted-foreground mt-2 italic">"{inferredData.description}"</p>
              )}
              <p className="text-xs text-orange-600 mt-2 capitalize">{inferredData.platform?.replace('_', ' ') || 'Unknown platform'}</p>
            </div>
          )}
          
          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                setSubmitted(false);
                setUrl('');
                setInferredData(null);
              }}
              className="bg-orange-500 text-white hover:bg-orange-600 rounded-lg px-6 py-3 font-medium transition-colors"
            >
              Submit Another
            </button>
            <a
              href="/events"
              className="text-orange-600 hover:text-orange-700 font-medium"
            >
              View Events
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="submit-meetup-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-900 via-purple-800 to-orange-600 py-12 sm:py-20">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-3">
            Add Your Meetup
          </h1>
          <p className="text-purple-100">
            Just paste your link - we'll do the rest
          </p>
        </div>
      </section>
      
      <div className="max-w-md mx-auto px-4 -mt-8">
        <form onSubmit={handleSubmit} className="bg-card border border-border rounded-xl shadow-lg p-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="url" className="text-sm font-medium text-foreground flex items-center gap-2">
                <Link2 className="w-4 h-4" />
                Meetup URL
              </label>
              <Input
                id="url"
                type="url"
                required
                placeholder="https://meetup.com/... or https://bitcoinwalk.org/..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="h-12"
                data-testid="meetup-url-input"
              />
              <p className="text-xs text-muted-foreground">
                Meetup.com, Bitcoin Walk, or any events page
              </p>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg py-3 font-medium transition-colors flex items-center justify-center gap-2"
              data-testid="submit-meetup-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Submit
                </>
              )}
            </button>
          </div>
        </form>
        
        {/* Supported Platforms */}
        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground mb-3">Supported platforms</p>
          <div className="flex justify-center gap-4 text-sm text-muted-foreground">
            <span>Meetup.com</span>
            <span>•</span>
            <span>Bitcoin Walk</span>
            <span>•</span>
            <span>Lu.ma</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmitMeetup;
